import { GroupOrderUniversal, LinkUniversal } from '@duxton-web/icons';
import { Card, Collapse, Flex, Table, Tag, Typography } from '@duxton-web/ui';
import React from 'react';
import { get } from 'lodash';

import { getHedwigTemplateUrl } from 'omega-rtc/utils/hedwig-template-url';
import './JourneyCampaignDetailSections.scss';

export type JourneyTemplateVariant = {
  name?: string;
  criteria?: Record<string, unknown> | null;
  template?: { ID?: string; type?: string } | null;
};

export type JourneyTemplateVariantGroup = {
  groupID: string;
  channel?: string;
  numberOfVariants?: number;
  experiment?: { controlGroupPercentage?: number } | null;
  variants?: JourneyTemplateVariant[] | null;
};

export const formatJourneyAttributeValue = (value: unknown): string => (
  value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value)
);

const getChannelLabel = (channel?: string) => {
  switch ((channel || '').toLowerCase()) {
    case 'push': return 'Push';
    case 'email': return 'Email';
    case 'pci':
    case 'pushinbox': return 'Push + Inbox';
    default: return channel || 'Not configured';
  }
};

const TemplateVariantTable = ({ group }: { group: JourneyTemplateVariantGroup }) => {
  const variants = Array.isArray(group.variants) ? group.variants : [];
  return (
    <Table
      className="journey-template-variant-table"
      dataSource={variants.map((variant, index) => ({ ...variant, rowKey: `${variant.name || 'variant'}-${index}` }))}
      pagination={false}
      rowKey="rowKey"
      size="small"
      onRow={(variant) => ({ 'aria-label': `Template variant ${variant.name || get(variant, 'template.ID') || 'unnamed'}` })}
      locale={{ emptyText: 'No variants are configured for this group.' }}
      columns={[
        {
          key: 'name',
          title: 'Variant name',
          dataIndex: 'name',
          render: (name) => name || '—',
        },
        {
          key: 'criteria',
          title: 'User attributes',
          dataIndex: 'criteria',
          render: (criteria) => (
            <Flex gap="6px" wrap="wrap">
              {criteria && typeof criteria === 'object' && !Array.isArray(criteria)
                ? Object.entries(criteria).map(([key, value]) => (
                  <Tag key={key}>{key}: {formatJourneyAttributeValue(value)}</Tag>
                )) : <Typography.Text type="secondary">No attributes</Typography.Text>}
            </Flex>
          ),
        },
        {
          key: 'template',
          title: 'Hedwig template',
          dataIndex: 'template',
          width: '27%',
          render: (template) => {
            const id = get(template, 'ID');
            const url = getHedwigTemplateUrl(id, get(template, 'type') || group.channel);
            return url ? (
              <a className="journey-template-variant-link" href={url} rel="noreferrer" target="_blank">
                <LinkUniversal aria-hidden="true" /><span>{id}</span>
              </a>
            ) : <Typography.Text>{id || 'No template link'}</Typography.Text>;
          },
        },
      ]}
    />
  );
};

const TemplateGroupDisclosure = ({ group }: { group: JourneyTemplateVariantGroup }) => {
  const variants = Array.isArray(group.variants) ? group.variants : [];
  const count = Array.isArray(group.variants) ? variants.length : group.numberOfVariants || 0;
  return (
    <Collapse
      bordered={false}
      className="journey-template-group-collapse"
      defaultActiveKey={[]}
      expandIconPosition="end"
      size="middle"
      items={[{
        key: group.groupID,
        label: (
          <Flex align="center" gap="12px" className="journey-template-group-heading">
            <Flex align="center" justify="center" className="journey-template-group-icon">
              <GroupOrderUniversal aria-hidden="true" />
            </Flex>
            <Flex vertical gap="2px" className="journey-template-group-identity">
              <Typography.Text type="secondary" className="journey-template-group-label">GROUP ID</Typography.Text>
              <Typography.Text strong className="journey-template-group-name">{group.groupID}</Typography.Text>
            </Flex>
          </Flex>
        ),
        extra: (
          <Flex align="center" gap="8px" wrap="wrap" className="journey-template-group-badges">
            <Tag color="purple">{getChannelLabel(group.channel)}</Tag>
            <Tag color="blue">{count} {count === 1 ? 'variant' : 'variants'}</Tag>
            <Tag color="gold">Control Group {get(group, 'experiment.controlGroupPercentage', 0)}%</Tag>
          </Flex>
        ),
        children: <TemplateVariantTable group={group} />,
      }]}
    />
  );
};

export const JourneyTemplateVariantMapping = ({ groups }: { groups: JourneyTemplateVariantGroup[] }) => (
  <Card className="journey-campaign-country-mappings" title="Template variant mapping">
    <Flex vertical gap="12px">
      {groups.length ? groups.map((group) => <TemplateGroupDisclosure key={group.groupID} group={group} />)
        : <Typography.Text type="secondary">No variants are configured.</Typography.Text>}
    </Flex>
  </Card>
);
